import React, { useMemo } from "react";
import { Button, Card, Space, Table, Tag, Tooltip, Typography } from "antd";

import {
    mapPricePeriodToWorkbenchRecord,
} from "../services/fuelPriceWorkbenchViewModel";
import PricePeriodActions from "./PricePeriodActions";

const { Text } = Typography;

const stopEvent = (event) => {
    event.stopPropagation();
};

const PriceLink = ({ value, record, onOpenComparison }) => {
    return (
        <Tooltip title="查看价格比较">
            <Button
                type="link"
                size="small"
                style={{ padding: 0, height: "auto" }}
                onClick={(event) => {
                    stopEvent(event);
                    onOpenComparison(record);
                }}
            >
                {value}
            </Button>
        </Tooltip>
    );
};

const PricePeriodTable = ({
    periods,
    loading,
    pagination,
    onTableChange,
    onViewDetail,
    onOpenOurPrice,
    onOpenSupplierQuote,
    onConfirm,
    onDispute,
    onDelete,
    onOpenComparison,
}) => {
    const records = useMemo(
        () => periods.map((period) => mapPricePeriodToWorkbenchRecord(period)),
        [periods]
    );

    const columns = [
        {
            title: "机场",
            dataIndex: "airport_code",
            key: "airport_code",
            width: 80,
            render: (code) => <Tag color="blue">{code}</Tag>,
        },
        {
            title: "供应商",
            key: "vendor",
            width: 150,
            render: (_, record) => record.vendorDisplayName,
        },
        {
            title: "周期",
            key: "period",
            width: 180,
            render: (_, record) => (
                <Text type="secondary" style={{ fontSize: 13 }}>
                    {record.periodRangeLabel}
                </Text>
            ),
        },
        {
            title: "我方预算价",
            dataIndex: "our_calculated_price",
            key: "our_calculated_price",
            width: 140,
            align: "right",
            render: (_, record) => (
                <Space size={4}>
                    <PriceLink
                        value={record.ourPriceDisplay}
                        record={record}
                        onOpenComparison={onOpenComparison}
                    />
                    <Tooltip title="编辑我方参考快照">
                        <Button
                            type="link"
                            size="small"
                            style={{ padding: 0, color: "#1677ff" }}
                            onClick={(event) => {
                                stopEvent(event);
                                onOpenOurPrice(record);
                            }}
                        >
                            编辑
                        </Button>
                    </Tooltip>
                </Space>
            ),
        },
        {
            title: "供应商确认价",
            dataIndex: "supplier_quoted_price",
            key: "supplier_quoted_price",
            width: 150,
            align: "right",
            render: (_, record) => (
                <Space size={4}>
                    <PriceLink
                        value={record.supplierPriceDisplay}
                        record={record}
                        onOpenComparison={onOpenComparison}
                    />
                    <Tooltip title="录入供应商报价快照">
                        <Button
                            type="link"
                            size="small"
                            style={{ padding: 0, color: "#d48806" }}
                            onClick={(event) => {
                                stopEvent(event);
                                onOpenSupplierQuote(record);
                            }}
                        >
                            录入
                        </Button>
                    </Tooltip>
                </Space>
            ),
        },
        {
            title: "差异",
            key: "difference",
            width: 110,
            align: "right",
            render: (_, record) => {
                if (record.price_difference == null) {
                    return <Text type="secondary">-</Text>;
                }

                return (
                    <Tooltip title="查看价格比较">
                        <Button
                            type="link"
                            size="small"
                            style={{ padding: 0, height: "auto", color: record.differenceColor }}
                            onClick={(event) => {
                                stopEvent(event);
                                onOpenComparison(record);
                            }}
                        >
                            {record.differencePercentDisplay}
                        </Button>
                    </Tooltip>
                );
            },
        },
        {
            title: "币种/单位",
            key: "unit",
            width: 100,
            render: (_, record) => (
                <Text type="secondary" style={{ fontSize: 12 }}>
                    {record.unitDisplay}
                </Text>
            ),
        },
        {
            title: "状态",
            key: "status",
            width: 100,
            render: (_, record) => (
                <Tag color={record.statusColor}>
                    {record.statusLabel}
                </Tag>
            ),
        },
        {
            title: "确认价",
            dataIndex: "final_confirmed_price",
            key: "final_confirmed_price",
            width: 120,
            align: "right",
            render: (_, record) => (
                <PriceLink
                    value={record.finalConfirmedDisplay}
                    record={record}
                    onOpenComparison={onOpenComparison}
                />
            ),
        },
        {
            title: "周期动作",
            key: "actions",
            width: 280,
            fixed: "right",
            render: (_, record) => (
                <PricePeriodActions
                    period={record}
                    onViewDetail={onViewDetail}
                    onOpenComparison={onOpenComparison}
                    onOpenOurPrice={onOpenOurPrice}
                    onOpenSupplierQuote={onOpenSupplierQuote}
                    onConfirm={onConfirm}
                    onDispute={onDispute}
                    onDelete={onDelete}
                />
            ),
        },
    ];

    return (
        <Card
            size="small"
            title="周期主表"
            extra={<Text type="secondary">点击价格列进入双快照比较浮层</Text>}
        >
            <Table
                rowKey="id"
                columns={columns}
                dataSource={records}
                loading={loading}
                pagination={{
                    ...pagination,
                    showSizeChanger: true,
                    showTotal: (total) => `共 ${total} 条`,
                }}
                onChange={onTableChange}
                scroll={{ x: 1540 }}
                size="small"
                onRow={(record) => ({
                    onClick: () => onViewDetail(record),
                    style: { cursor: "pointer" },
                })}
            />
        </Card>
    );
};

export default PricePeriodTable;
